/**
 * Permission Components
 * 
 * Provides permission-based access control for routes and UI elements.
 * 
 * Usage:
 * 
 * // Protect a route
 * <PermissionRoute resource="billing" action="view">
 *   <BillingPage />
 * </PermissionRoute>
 * 
 * // Hide a button
 * <PermissionGate resource="clients" action="create">
 *   <Button>New Client</Button>
 * </PermissionGate>
 * 
 * // Show alternative content if no permission
 * <PermissionGate resource="billing" action="view" fallback={<AccessDenied />}>
 *   <BillingData />
 * </PermissionGate>
 */

import { Navigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { ShieldX } from 'lucide-react';

/**
 * PermissionRoute - Protects entire routes based on permissions
 * Redirects to home or shows access denied if user lacks permission
 */
export function PermissionRoute({ 
  resource, 
  action, 
  roles = null,
  children, 
  redirectTo = '/',
  showAccessDenied = false 
}) {
  const { hasPermission, user, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="animate-spin h-8 w-8 border-2 border-primary border-t-transparent rounded-full" />
      </div>
    );
  }
  
  // `roles` (when provided) gates by user role; otherwise fall back to a
  // permission check. Admins always pass.
  const allowed = roles
    ? (user?.role === 'admin' || roles.includes(user?.role))
    : hasPermission(resource, action);

  if (!allowed) {
    if (showAccessDenied) {
      return <AccessDenied resource={resource} action={action} />;
    }
    return <Navigate to={redirectTo} replace />;
  }
  
  return children;
}

/**
 * PermissionGate - Conditionally renders children based on permissions
 * Use for hiding buttons, sections, or sensitive data
 */
export function PermissionGate({ 
  resource, 
  action, 
  children, 
  fallback = null,
  // Allow checking multiple permissions (OR logic)
  anyOf = null,
  // Allow checking multiple permissions (AND logic)  
  allOf = null
}) {
  const { hasPermission } = useAuth();
  
  let hasAccess = false;
  
  if (anyOf && anyOf.length > 0) {
    // User needs ANY of the listed permissions
    hasAccess = anyOf.some(({ resource: r, action: a }) => hasPermission(r, a));
  } else if (allOf && allOf.length > 0) {
    // User needs ALL of the listed permissions
    hasAccess = allOf.every(({ resource: r, action: a }) => hasPermission(r, a));
  } else if (resource && action) {
    // Single permission check
    hasAccess = hasPermission(resource, action);
  }
  
  if (!hasAccess) {
    return fallback;
  }
  
  return children;
}

/**
 * AdminOnly - Shorthand for admin-only content
 */
export function AdminOnly({ children, fallback = null }) {
  const { hasPermission } = useAuth();
  
  const isAdmin = hasPermission('admin', 'systemSettings') || 
                  hasPermission('admin', 'manageUsers');
  
  if (!isAdmin) {
    return fallback;
  }
  
  return children;
}

/**
 * CanView - Shorthand for view permission check
 */
export function CanView({ resource, children, fallback = null }) {
  return (
    <PermissionGate resource={resource} action="view" fallback={fallback}>
      {children}
    </PermissionGate>
  );
}

/**
 * CanCreate - Shorthand for create permission check
 */
export function CanCreate({ resource, children, fallback = null }) {
  return (
    <PermissionGate resource={resource} action="create" fallback={fallback}>
      {children}
    </PermissionGate>
  );
}

/**
 * CanEdit - Shorthand for edit permission check
 */
export function CanEdit({ resource, children, fallback = null }) {
  return (
    <PermissionGate resource={resource} action="edit" fallback={fallback}>
      {children}
    </PermissionGate>
  );
}

/**
 * CanDelete - Shorthand for delete permission check
 */
export function CanDelete({ resource, children, fallback = null }) {
  return (
    <PermissionGate resource={resource} action="delete" fallback={fallback}>
      {children}
    </PermissionGate>
  );
}

/**
 * AccessDenied - Display when user lacks permission
 */
export function AccessDenied({ resource, action }) {
  return (
    <div className="flex flex-col items-center justify-center min-h-[400px] text-center p-8">
      <div className="h-16 w-16 rounded-full bg-destructive/10 flex items-center justify-center mb-4">
        <ShieldX className="h-8 w-8 text-destructive" />
      </div>
      <h2 className="text-xl font-semibold mb-2">Access Denied</h2>
      <p className="text-muted-foreground max-w-md">
        You don't have permission to access this area. 
        {resource && action && (
          <span className="block mt-1 text-sm">
            Required: {resource}.{action}
          </span>
        )}
      </p>
      <p className="text-sm text-muted-foreground mt-4">
        Contact your administrator if you believe this is an error.
      </p>
    </div>
  );
}

/**
 * usePermissions - Hook for programmatic permission checks
 */
export function usePermissions() {
  const { hasPermission, user } = useAuth();
  
  return {
    hasPermission,
    isAdmin: hasPermission('admin', 'systemSettings'),
    canManageUsers: hasPermission('admin', 'manageUsers'),
    canViewAuditLog: hasPermission('admin', 'viewAuditLog'),
    canViewBilling: hasPermission('billing', 'view'),
    canCreateBilling: hasPermission('billing', 'create'),
    canAssignTickets: hasPermission('tickets', 'assignToOthers'),
    canViewReports: hasPermission('reports', 'view') || hasPermission('reports', 'viewOperational'),
    canViewFinancialReports: hasPermission('reports', 'viewFinancial'),
    canViewOperationalReports: hasPermission('reports', 'viewOperational'),
    canExportCsv: hasPermission('reports', 'exportCsv'),
    role: user?.role,
    userId: user?.id,
    permissions: user?.permissions || {},
  };
}

export default PermissionGate;
